import { getMoscowDateTime } from '../../../utils/getMoscowDateTime'

export type QuickRangeId =
  | 'currentShift'
  | 'prevShift'
  | '24hours'
  | '7days'
  | '30days'
  | 'today'
  | 'yesterday'

export interface QuickRange {
  from: Date
  to: Date
}

const MS_DAY = 24 * 60 * 60 * 1000

/**
 * Compute a `{ from, to }` window for a quick-range button on the
 * `DateRangeFilter`. Logic mirrors the legacy `DateTimeRangeFilter` 1-to-1 so
 * UX doesn't drift; shifts are anchored to 08:00 / 20:00 boundaries.
 *
 * The window is anchored to Moscow time regardless of the browser timezone —
 * the whole admin panel treats picked wall-clock values as MSK and converts
 * them with `convertToUTCByMSK` at the API boundary.
 *
 * @param id quick-range identifier
 * @returns inclusive `{from, to}` window in Moscow wall-clock time
 */
export function calculateQuickRange(id: QuickRangeId): QuickRange {
  const now = getMoscowDateTime()
  const shift: 'day' | 'night' = now.getHours() >= 8 && now.getHours() < 20 ? 'day' : 'night'

  let from = new Date(now)
  let to   = new Date(now)

  switch (id) {
    case 'currentShift': {
      if (shift === 'day') {
        from.setHours(8, 0, 0, 0)
      } else {
        // Night shift starts at 20:00; if we're between 00:00 and 08:00 it began yesterday
        if (now.getHours() < 20) {
          from = new Date(now.getTime() - MS_DAY)
        }
        from.setHours(20, 0, 0, 0)
      }
      break
    }
    case 'prevShift': {
      if (shift === 'day') {
        // Currently in day shift → previous = yesterday night 20:00 → today 08:00
        from = new Date(now.getTime() - MS_DAY)
        from.setHours(20, 0, 0, 0)
        to.setHours(8, 0, 0, 0)
      } else {
        // Currently in night shift → previous = today's day shift 08:00 → 20:00
        from.setHours(8, 0, 0, 0)
        to.setHours(20, 0, 0, 0)
      }
      break
    }
    case '24hours': {
      from = new Date(now.getTime() - MS_DAY)
      break
    }
    case '7days': {
      from = new Date(now.getTime() - 7 * MS_DAY)
      break
    }
    case '30days': {
      from = new Date(now.getTime() - 30 * MS_DAY)
      break
    }
    case 'today': {
      from.setHours(8, 0, 0, 0)
      break
    }
    case 'yesterday': {
      from = new Date(now.getTime() - MS_DAY)
      from.setHours(8, 0, 0, 0)
      to.setHours(8, 0, 0, 0)
      break
    }
    default: {
      const _exhaustive: never = id
      void _exhaustive
    }
  }

  return { from, to }
}
